import { Button, Form } from '@ampol/design-system';
import { CardModalFormNames } from './types/CardModalFormNames';
import { ModalSection } from './ModalSection';
import { VERIFICATION_METHODS } from 'utilities/constants';
import { useSelector } from 'react-redux';
import { useSelectCardModalMeta } from 'selectors/selectors';
import { isWithinDaysBeforeExpiry } from 'utilities/dateUtils';
import { EXPIRY_DAYS_WARNING_THRESHOLD } from '../../../../constants';
import { getCardStatus } from 'utilities/card-utilities';
import Env from 'Env';
import { CardModalFormValues } from './types/CardModalFormNames';
import {
  getEffectiveCreditLimit,
  filterRestrictedDailyDollarLimits,
  filterDailyDollarLimitsByCreditLimit,
} from 'features/cards/helpers/card-limit-utilities';

interface CardModalUsageOptionsProps {
  readOnly?: boolean;
  hideFavourites?: boolean;
  showFavouriteAddButton?: boolean;
  showFavouriteNameInput?: boolean;
  onClickAddFavourite?: () => void;
  isFavouritesLoading?: boolean;
  isEdit?: boolean;
  limitMode?: 'add' | 'edit';
  favouriteMode?: 'apply' | 'manage';
  contractNumber?: string | string[];
}

interface CardFavouriteOption {
  id: string;
  favouriteName: string;
}

export default function CardModalUsageOptions({
  readOnly = false,
  hideFavourites = false,
  showFavouriteNameInput = false,
  showFavouriteAddButton = false,
  onClickAddFavourite,
  isFavouritesLoading,
  isEdit = false,
  limitMode,
  favouriteMode,
  contractNumber,
}: CardModalUsageOptionsProps) {
  const meta = useSelectCardModalMeta();
  const allAccounts = useSelector((state: any) => state.accountsStore?.allAccounts ?? []);
  const formValues = useSelector((state: any) => (state.cardsStore?.formValues ?? {}) as CardModalFormValues);
  const cardOptions = useSelector((state: any) => state.getCardOptions.cardOptions.profiles);
  const accountFavouritesData =
    cardOptions?.map((item: CardFavouriteOption) => ({
      label: item.favouriteName,
      value: item.id,
    })) ?? [];

  const selectedContractNumber = contractNumber ?? formValues.contractNumber;
  const accountCreditLimit = getEffectiveCreditLimit(allAccounts, selectedContractNumber);
  const allowedDailyDollarLimits =
    limitMode || isEdit || favouriteMode === 'manage'
      ? filterRestrictedDailyDollarLimits(meta?.dailyDollarLimits)
      : meta?.dailyDollarLimits;
  const filteredDailyDollarLimits = filterDailyDollarLimitsByCreditLimit(allowedDailyDollarLimits, accountCreditLimit);
  const shouldFilterDailyLimits = typeof Env.getIsCardLimitsReadOnly === 'function' && Env.getIsCardLimitsReadOnly();
  const dailyDollarLimitsData = (shouldFilterDailyLimits ? filteredDailyDollarLimits : allowedDailyDollarLimits)?.map(
    (item) => ({
      label: item.code === '999999999' ? '$999999999 (No Limit)' : `$${item.code}`,
      value: item.code.toString(),
    })
  );

  const monthlyDollarLimitsData = meta?.monthlyDollarLimits?.map((item) => ({
    label: item.code === '999999999' ? '$999999999 (No Limit)' : `$${item.code}`,
    value: item.code.toString(),
  }));

  const isWithinThreshold = isWithinDaysBeforeExpiry(EXPIRY_DAYS_WARNING_THRESHOLD);
  const isStatusCardProcessing = getCardStatus() === 'Processing';
  return (
    <div>
      <h3>Card usage options</h3>
      <ModalSection first>
        {!hideFavourites && (
          <Form.DropdownSearch
            name={CardModalFormNames.Favourite}
            allowClear={false}
            data={accountFavouritesData}
            label='Favourite'
            placeholder='Select favourite'
            loading={isFavouritesLoading}
          />
        )}
        {showFavouriteAddButton && (
          <Button
            className='mt-auto'
            label={'Add new favourite'}
            variant={'primary'}
            color='blue'
            onClick={onClickAddFavourite}
          />
        )}
        {showFavouriteNameInput && (
          <Form.Input
            label='Enter Card Favourite Name'
            name='cardFavoriteName'
            className={'card-usage-options-form__uppercase'}
          />
        )}
      </ModalSection>

      <br />
      <strong>Point of Sale (POS) prompts</strong>
      <ModalSection first>
        <Form.DropdownSearch
          name={CardModalFormNames.VerificationMethod}
          label='Verification Method'
          data={VERIFICATION_METHODS}
          placeholder='Select verification method'
          disabled={readOnly || (meta?.verifyPosPrompt === 'Mandatory PIN' ? true : false)}
        />
      </ModalSection>
      <Form.Checkbox
        name={CardModalFormNames.OdometerRequired}
        label='Odometer required'
        color='blue'
        className='!mt-xs'
        disabled={readOnly || (isEdit && (isWithinThreshold || isStatusCardProcessing))}
      />
      <Form.Checkbox
        name={CardModalFormNames.OrderNumberRequired}
        label='Order number required'
        color='blue'
        className='!mt-xs'
        disabled={readOnly || (isEdit && (isWithinThreshold || isStatusCardProcessing))}
      />
      <br />
      <strong>Purchase controls</strong>
      <ModalSection first last>
        <Form.DropdownSearch
          name={CardModalFormNames.DailyLimit}
          data={dailyDollarLimitsData}
          filterOptions
          label='Daily limit'
          placeholder='Select daily dollar limit'
          disabled={readOnly}
        />
        <Form.DropdownSearch
          name={CardModalFormNames.MonthlyLimit}
          data={monthlyDollarLimitsData}
          filterOptions
          label='Monthly limit'
          placeholder='Select monthly dollar limit'
          disabled={readOnly}
        />
      </ModalSection>
    </div>
  );
}
